import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from "@material-ui/core";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import NameField from "./formFields/NameField";
import ApiKeyField from "./formFields/ApiKeyField";
import PromptField from "./formFields/PromptField";
import QueueSelectSingle from "./formFields/QueueSelectSingle";
import ModelField from "./formFields/ModelField";
import TemperatureAndMaxFields from "./formFields/TemperatureAndMaxFields";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexWrap: "wrap" },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": { marginRight: theme.spacing(1) },
  },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: theme.palette.success.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const PromptSchema = Yup.object().shape({
  name: Yup.string().required(i18n.t("promptModal.validation.name")),
  apiKey: Yup.string().required(i18n.t("promptModal.validation.apiKey")),
  prompt: Yup.string().required(i18n.t("promptModal.validation.prompt")),
  temperature: Yup.number().min(0).max(1).required(i18n.t("promptModal.validation.temperature")),
  maxTokens: Yup.number().required(i18n.t("promptModal.validation.maxTokens")),
  maxMessages: Yup.number().required(i18n.t("promptModal.validation.maxMessages")),
  model: Yup.string().required(i18n.t("promptModal.validation.model"))
});

const PromptModal = ({
  open,
  onClose,
  promptId,
  refreshPrompts,
  modelList,
  atualizarModelosDaOpenAI
}) => {
  const classes = useStyles();
  const [initialValues, setInitialValues] = useState(null);
  const [apiKeysList, setApiKeysList] = useState([]);

  const handleClose = () => {
    onClose();
    setInitialValues(null);
  };

  const handleSavePrompt = async (values) => {
    try {
      if (promptId) {
        await api.put(`/prompt/${promptId}`, values);
      } else {
        await api.post("/prompt", values);
      }
      toast.success(i18n.t("promptModal.success"));
      refreshPrompts();
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  useEffect(() => {
    let isMounted = true;
  
    const fetchData = async () => {
      try {
        const { data: keysData } = await api.get("/apikey");
        if (!isMounted) return;
  
        const keys = Array.isArray(keysData) ? keysData : keysData.apiKeys || [];
        setApiKeysList(keys);
  
        if (promptId) {
          const { data } = await api.get(`/prompt/${promptId}`);
          if (!isMounted) return;
  
          const apiKeyId = typeof data.apiKey === 'object' ? data.apiKey.id : data.apiKey;
  
          setInitialValues({
            name: data.name || "",
            apiKey: apiKeyId || "",
            queueId: data.queueId || "",
            prompt: data.prompt || "",
            model: data.model || "",
            temperature: data.temperature ?? 0.7,
            maxTokens: data.maxTokens ?? 1000,
            maxMessages: data.maxMessages ?? 10
          });
        } else {
          setInitialValues({
            name: "",
            apiKey: keys.length === 1 ? keys[0].id : "",
            queueId: "",
            prompt: "",
            model: "",
            temperature: 0.7,
            maxTokens: 1000,
            maxMessages: 10
          });
        }
      } catch (err) {
        if (isMounted) toastError(err);
      }
    };
  
    if (open) fetchData();
  
    return () => {
      isMounted = false;
    };
  }, [promptId, open]);
  

  if (!initialValues || !apiKeysList.length) return null;

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" scroll="paper" fullWidth>
        <DialogTitle>
          {promptId ? i18n.t("promptModal.title.edit") : i18n.t("promptModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={PromptSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSavePrompt(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                <NameField touched={touched} errors={errors} label={i18n.t("promptModal.fields.name")} />
                <ApiKeyField
                  values={values}
                  touched={touched}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  apiKeysList={apiKeysList}
                  label={i18n.t("promptModal.fields.apiKey")}
                />
                <PromptField touched={touched} errors={errors} label={i18n.t("promptModal.fields.prompt")} />
                <QueueSelectSingle values={values} touched={touched} errors={errors} setFieldValue={setFieldValue} label={i18n.t("promptModal.fields.queue")} />
                <ModelField setFieldValue={setFieldValue} label={i18n.t("promptModal.fields.model")} />
                <TemperatureAndMaxFields
                  touched={touched}
                  errors={errors}
                  classes={classes}
                  labelTemp={i18n.t("promptModal.fields.temperature")}
                  labelMaxTokens={i18n.t("promptModal.fields.maxTokens")}
                  labelMaxMessages={i18n.t("promptModal.fields.maxMessages")}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="secondary" variant="outlined" disabled={isSubmitting}>
                  Cancelar
                </Button>
                <div className={classes.btnWrapper}>
                  <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                    {promptId ? "Salvar" : "Criar"}
                  </Button>
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </div>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default PromptModal;